import { useRef } from "react";
import { motion } from "framer-motion";
import { useReveal } from "@/hooks/useReveal";
import { SpotlightMask } from "@/components/ui/spotlight-mask";
import { InViewTextEffect } from "@/components/ui/text-effect";
import {
  Fingerprint,
  Monitor,
  Smartphone,
  Database,
  Gauge,
  Wrench,
  Search,
  BookOpen,
} from "lucide-react";

// Spec-sheet card language pulled from Letraset process sheets: a symbol, a
// name set in display type, a one-line spec, and a mono scope tag — meant to
// read like something printed and reviewed, not a SaaS pricing grid.
const DELIVERABLES = [
  {
    icon: Fingerprint,
    name: "Brand Identity System",
    desc: "Mark, palette, and type logic built to hold up across every surface.",
    scope: "Included in Growth",
  },
  {
    icon: Monitor,
    name: "Custom Website Design",
    desc: "A layout system designed from your material, not a template.",
    scope: "Included in Growth",
  },
  {
    icon: Smartphone,
    name: "Responsive Build",
    desc: "Tested at real breakpoints, not just resized in a browser.",
    scope: "Included in Growth",
  },
  {
    icon: Database,
    name: "CMS Integration",
    desc: "Content you can edit without touching code or calling us.",
    scope: "Custom",
  },
  {
    icon: Gauge,
    name: "Performance Audit",
    desc: "Core Web Vitals review with a prioritized fix list.",
    scope: "Custom",
  },
  {
    icon: Wrench,
    name: "Monthly Maintenance",
    desc: "Updates, monitoring, and small changes handled on retainer.",
    scope: "Custom",
  },
  {
    icon: Search,
    name: "SEO Foundation",
    desc: "Structure, metadata, and sitemap work done at launch, not after.",
    scope: "Included in Growth",
  },
  {
    icon: BookOpen,
    name: "Design System Documentation",
    desc: "Tokens and components written down so the system outlives one build.",
    scope: "Custom",
  },
];

// Faint topo-line SVG pattern, brand motif — one constant near-invisible
// base layer, one identical layer at full strength that's only visible
// where SpotlightMask's cursor-tracked mask reveals it (`revealed` prop).
function TopoTexture({ revealed = false }) {
  const paths = [20, 45, 72, 100, 130, 160, 185].map((y, i) => (
    <path
      key={y}
      d={`M0 ${y} Q 50 ${y - 14 + (i % 2) * 8} 100 ${y} T 200 ${y}`}
      fill="none"
      stroke="#2F4A3E"
      strokeWidth="1"
    />
  ));

  if (!revealed) {
    return (
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.04]"
        viewBox="0 0 200 200"
        preserveAspectRatio="none"
      >
        {paths}
      </svg>
    );
  }

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full opacity-40"
      viewBox="0 0 200 200"
      preserveAspectRatio="none"
      style={{
        WebkitMaskImage:
          "radial-gradient(var(--spot-size) circle at var(--spot-x) var(--spot-y), black, transparent)",
        maskImage:
          "radial-gradient(var(--spot-size) circle at var(--spot-x) var(--spot-y), black, transparent)",
      }}
    >
      {paths}
    </svg>
  );
}

function SpecCard({ item }) {
  const Icon = item.icon;
  return (
    // data-anim (scroll-in stagger) lives on this wrapper; whileHover (lift)
    // stays on the inner motion.div — separate nodes so GSAP and Framer
    // never write to the same element's transform in the same frame.
    <div data-anim>
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden rounded-lg border border-hairline bg-paper/90 backdrop-blur-sm shadow-[0_1px_0_rgba(43,42,38,0.06)]"
      >
        <SpotlightMask size={180} className="p-6">
          <TopoTexture />
          <TopoTexture revealed />
          <Icon size={20} strokeWidth={1.5} className="relative text-pine" />
          <h3 className="relative mt-4 font-serif text-2xl text-ink">{item.name}</h3>
          <p className="relative mt-2 font-sans text-sm text-ink-dim">{item.desc}</p>
          <span className="relative mt-4 inline-block font-mono text-[11px] uppercase tracking-[0.14em] text-pine/80">
            {item.scope}
          </span>
        </SpotlightMask>
      </motion.div>
    </div>
  );
}

export default function SpecCards() {
  const gridRef = useRef(null);
  useReveal(gridRef);

  return (
    <div className="mx-auto max-w-6xl px-5 py-14 md:px-8">
      <header className="mb-10 max-w-xl">
        <InViewTextEffect
          as="p"
          preset="blur"
          className="font-mono text-xs uppercase tracking-[0.18em] text-pine/70"
        >
          Deliverables
        </InViewTextEffect>
        <h2 className="mt-2 font-serif text-3xl text-ink md:text-4xl">
          What you actually get
        </h2>
      </header>

      <div ref={gridRef} data-stagger className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {DELIVERABLES.map((item) => (
          <SpecCard key={item.name} item={item} />
        ))}
      </div>
    </div>
  );
}
